import type { ReportMetadata } from '../../types';
import type { Planungsfrage, Planungsphase, SigeSchrittDokumente } from '../../components/planungsphase/types';
import { describeError } from '../errorMessage';
import { fetchPlanungsLage } from '../planungsphaseService';
import { fetchSigePlanDokumente } from '../sigePlanDokumenteService';
import { OHNE_LESEFEHLER, type SteckbriefLesefehler, type SteckbriefQuellen } from './steckbrief';

/**
 * Laedt die Quellen des Projektsteckbriefs — NUR LESEN, NICHTS ANLEGEN.
 *
 * ═══ FAIL-OPEN, ABER NIE STILL ════════════════════════════════════════════
 * Ein Lesefehler (Tabelle fehlt, Netz weg, Regel greift) wirft nicht und
 * liefert auch keine leere Lage, die wie „alles geklaert" aussaehe. Er steht
 * als `lesefehler` je Quelle in den Quellen; `baueSteckbrief` schreibt ihn in
 * den Grund jeder betroffenen Angabe („konnte nicht gelesen werden" statt
 * „noch nicht angelegt"). Das Versprechen dieser Funktion: sie lehnt nie ab.
 *
 * ═══ WAS GELESEN WIRD ═════════════════════════════════════════════════════
 *   - Planungsphase mit Fragen (`fetchPlanungsLage`, legt nichts an).
 *   - Mitgeltende Dokumente des aktuellen SiGe-Plans (`fetchSigePlanDokumente`,
 *     selbst fail-open) — fuer die Spalte „im SiGe-Plan" der Unterlagen.
 *   - Die Projektdaten kommen vom Aufrufer (`report.metadata`), sie liegen in
 *     jedem Modul schon vor; ein zweiter Leseweg koennte einen aelteren Stand
 *     zeigen als die Maske daneben.
 */

/** Quellen ohne Datenbankzugriff — fuer `temp-id`, Tests und den Leerzustand. */
export const leereSteckbriefQuellen = (metadata: Partial<ReportMetadata> | null): SteckbriefQuellen => ({
    metadata,
    phase: null,
    fragen: [],
    sigeSchritte: [],
    lesefehler: { ...OHNE_LESEFEHLER },
});

/** Ergebnis eines Leseversuchs — nie eine Ablehnung, immer ein Wert. */
type Versuch<T> = { ok: true; wert: T } | { ok: false; fehler: string };

const versuche = <T>(p: Promise<T>): Promise<Versuch<T>> =>
    p.then(
        (wert): Versuch<T> => ({ ok: true, wert }),
        (e: unknown): Versuch<T> => ({ ok: false, fehler: describeError(e) }),
    );

export const ladeSteckbriefQuellen = async (
    projectId: string,
    metadata: Partial<ReportMetadata> | null,
): Promise<SteckbriefQuellen> => {
    if (!projectId || projectId === 'temp-id') return leereSteckbriefQuellen(metadata);

    const lesefehler: SteckbriefLesefehler = { ...OHNE_LESEFEHLER };
    let phase: Planungsphase | null = null;
    let fragen: Planungsfrage[] = [];
    let sigeSchritte: SigeSchrittDokumente[] = [];

    const [lage, sige] = await Promise.all([
        versuche(fetchPlanungsLage(projectId, { streng: true })),
        versuche(fetchSigePlanDokumente(projectId)),
    ]);

    // `=== true` statt Wahrheitswert: ohne `strict` verengt tsc die Union
    // sonst nicht, und `fehler` waere im else-Zweig unbekannt.
    if (lage.ok === true) {
        phase = lage.wert.phase;
        fragen = lage.wert.fragen;
    } else {
        lesefehler.planungsphase = lage.fehler;
    }

    if (sige.ok === true) {
        sigeSchritte = sige.wert.schritte;
        // `fetchSigePlanDokumente` wirft nicht, sondern meldet im Feld.
        if (sige.wert.lesefehler) lesefehler.sigeplan = sige.wert.lesefehler;
    } else {
        lesefehler.sigeplan = sige.fehler;
    }

    return { metadata, phase, fragen, sigeSchritte, lesefehler };
};

/** Lesefehler als ein Satz fuer die Oberflaeche — `null`, wenn alles gelesen wurde. */
export const lesefehlerText = (l: SteckbriefLesefehler | null | undefined): string | null => {
    if (!l) return null;
    const teile: string[] = [];
    if (l.planungsphase) teile.push(`Planungsphase: ${l.planungsphase}`);
    if (l.sigeplan) teile.push(`SiGe-Plan: ${l.sigeplan}`);
    return teile.length ? teile.join(' · ') : null;
};
